import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  DOM_CHANGED_EVENT,
  triggerEvent,
  TRACK_INTERACTION,
  SWIPEABLE_CONTENT_CHANGED,
} from '@fec/assets/js/utils/event';
import Duration from '@fec/frontend/elements/media/duration/duration';
import ShowMoreBar from '@fec/frontend/modules/groups-and-lists/show-more-bar/show-more-bar';
import Swipeable from '@fec/frontend/snowflakes/search/swipeable/swipeable';
import PlayIcon from '@fec/frontend/elements/icons/play-icon/PlayIcon';
import { truncate } from '@fec/assets/js/utils/string-formatter';
import {
  ACTION_NEXT_PAGE,
  searchContextStore,
} from '../search/SearchContextProvider';
import NoResults from './NoResults';
import SimpleFetchClient from '../search/SimpleFetchClient';
import { translate } from '../searchHelpers';
import { PlayAudioClient } from '../search/PlayAudioClient';
import CollectionHeading from './CollectionHeading';
import { CollectionNgSwipeable } from '@fec/frontend/modules/collections/collection-ng/collection-ng-swipeable';
import { getConfig } from '@fec/assets/js/utils/config.js';
import ImageNg from '@fec/frontend/elements/media/image-ng/ImageNg';

import '@aron/search/js/styles/episode-list.scss';
import '@aron/search/js/styles/episode-listing.scss';
import '@aron/search/js/styles/media-still.scss';

const { IL_BASE_URL } = getConfig();

export const AudioResults = () => {
  const [searchResultEpisodes, setSearchResultEpisodes] = useState(null);
  const [searchResultShows, setSearchResultShows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const { state: searchContext, dispatch } = useContext(searchContextStore);
  const _t = translate.bind(
    null,
    searchContext.searchConfig,
    searchContext.translations,
  );

  const loadMore = (event) => {
    event.preventDefault();
    if (!loading) {
      dispatch({ type: ACTION_NEXT_PAGE });

      triggerEvent(TRACK_INTERACTION, {
        event_source: 'search_audio',
        event_name: searchContext.searchParams.q,
        event_value: 'load_more-' + (searchContext.searchParams.page * 1 + 1),
        event_type: 'hidden_event',
      });
    }
  };

  const audioSearchClient = new PlayAudioClient(
    searchContext.searchConfig.audioSearch,
  );

  const audioShowsClient = new SimpleFetchClient(
    searchContext.searchConfig.audioSearch.showSearchEndpoint,
  );
  const search = (params) => {
    if (params.q === '') {
      // Reset search results if search string is reset
      setSearchResultEpisodes(null);
      setSearchResultShows(null);
      return;
    }

    setLoading(true);

    audioSearchClient
      .search(params)
      .then((result) => {
        setSearchResultEpisodes(result);
      })
      .catch((error) => {
        // eslint-disable-next-line
        console.warn('Error fetching search results for Audio media:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    // Is needed to apply jQuery specific handling (when new dom nodes appear)
    triggerEvent(DOM_CHANGED_EVENT);
  }, [searchResultEpisodes]);

  // Episode search is triggered if date, page or queryTerm changes
  useEffect(() => {
    search(searchContext.searchParams);
  }, [searchContext.searchParams]);

  useEffect(() => {
    if (searchContext.searchParams.q === '') {
      return;
    }
    setSearchResultShows([]);

    audioShowsClient
      .fetchWithParams([{ param: 'q', value: searchContext.searchParams.q }])
      .then((result) => {
        setSearchResultShows(result);
      })
      .catch((error) => {
        // eslint-disable-next-line
        console.warn('Error searching shows:', error);
      });
  }, [searchContext.searchParams.q]);

  useEffect(() => {
    const rubricSearchRoot = document.getElementById('rubric-search');
    setBaseUrl(rubricSearchRoot.dataset.baseUrl);
  }, []);

  return (
    <>
      <AudioShowsResults
        searchResultShows={searchResultShows}
        searchContext={searchContext}
        translate={_t}
      />

      <AudioEpisodesResults
        searchResultEpisodes={searchResultEpisodes}
        searchContext={searchContext}
        baseUrl={baseUrl}
        loadMore={loadMore}
        loading={loading}
        translate={_t}
      />
    </>
  );
};

const AudioResult = ({
  title,
  show,
  date,
  lead,
  description,
  duration,
  imageUrl,
  imageTitle,
  baseUrl = '',
  id,
  translate,
  searchContext,
}) => {
  const [hover, setHover] = useState(false);

  const displayDate = new Date(date).toLocaleDateString(
    searchContext.searchConfig.locale,
  );

  // Prefer lead to description
  const text = lead ? lead : description;
  // translate predicate before show name
  const getPredicate = (name) => {
    let translatedPredicate = 'von ';
    if (searchContext.searchConfig.audioSearch.bu === 'rtr') {
      if (/[aeiouäöüèéàì]/.test(name.charAt(0).toLowerCase())) {
        translatedPredicate = 'dad ';
      } else {
        translatedPredicate = 'da ';
      }
    }
    return translatedPredicate;
  };

  const imgSrc = `${IL_BASE_URL}/images/?imageUrl=${imageUrl.replace(
    'WEBVISUAL',
    'PODCAST',
  )}&format=jpg&width=480`;

  const imageObject = {
    provider: 'il',
    id: imgSrc,
    alt: imageTitle,
  };

  return (
    <a
      className="episode-listing"
      href={`${baseUrl}/play/radio/popupaudioplayer?id=${id}`}
      data-event-track={`event_source=podcasts_search&event_type=click&event_name=${title}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      <div className="medium medium--episode-listing medium--no-bg">
        <div className="medium__object">
          <div className="media-still media-still--dynamic js-media-progress-container">
            <div className="media-still__image">
              <ImageNg image={imageObject} />
            </div>
            {hover && (
              <div className="media-still__icon">
                <PlayIcon type="audio" />
              </div>
            )}
          </div>
        </div>

        <div className="medium__caption">
          <div className="media-caption media-caption--episode-listing">
            <span className="h-offscreen">Audio</span>
            <div className="media-caption__title media-caption__title--audio-search-result">
              {title}
            </div>

            <div className="media-caption__source media-caption__source--audio">
              <strong>
                <Duration duration={Math.floor(duration / 1000)} />
              </strong>
              {` min, ${getPredicate(show.title)} `}
              <strong className="media-caption__source-title">
                {show.title}
              </strong>
              {` ${translate('vom').replace('%date%', '')} ${displayDate}`}
            </div>

            <div className="media-caption__description">
              {truncate(text, 170)}
            </div>
          </div>
        </div>
      </div>
    </a>
  );
};

const AudioShowsResults = ({ searchResultShows, searchContext, translate }) => {
  // we use collection-ng-swipeable.js to initialize the swipeable component, so we need a reference to the DOM element
  const swipeableNgCollection = useRef(null);
  // we need to remember if we already initialized the CollectionNgSwipeable to avoid reinitializing the swipeable component
  const swipeabilityInitialized = useRef(false);

  useEffect(() => {
    // this useEffect does 2 things:
    // 1. it initializes the swipeable component the first time it's rendered
    // 2. it triggers an event when the items change, so the swipeable component can update itself
    if (swipeableNgCollection.current && searchResultShows.length > 0) {
      if (!swipeabilityInitialized.current) {
        new CollectionNgSwipeable(swipeableNgCollection.current);
        swipeabilityInitialized.current = true;
      } else {
        triggerEvent(
          SWIPEABLE_CONTENT_CHANGED,
          null,
          swipeableNgCollection.current,
        );
      }
    }
  }, [
    swipeableNgCollection.current,
    searchResultShows,
    swipeabilityInitialized.current,
  ]);

  if (!searchResultShows || searchResultShows.length === 0) {
    return null;
  }

  const items = searchResultShows.map((item) => {
    const newItem = Object.assign({}, item);
    newItem['urn:'] = item.urn; // No fkng idea why this needs to be set this weird way
    return newItem;
  });

  const title = translate('audioShowsTitle').replace(
    // quick and dirty: JMSTranslate has problems with '&', so this will be replaced here
    '%brandName%',
    searchContext.searchConfig.audioSearch.srfBrandName,
  );

  return (
    <div
      ref={swipeableNgCollection}
      className="collection-ng collection-ng--dense-swipeable collection-ng--branded collection-ng--theme-neutral-strong js-collection-ng-swipeable">
      <CollectionHeading
        title={title}
        emphasizedTitlePart={searchContext.searchParams.q}
      />

      <Swipeable
        translations={{ playSubtitle: '' }}
        visuallyHidden={items.length === 0}>
        {items.map((item, i) => (
          <AudioShow item={item} key={i} />
        ))}
      </Swipeable>
    </div>
  );
};

const AudioShow = ({ item }) => {
  let imageSrc = item.imageSquare;
  if (item.imageSquare?.variations?.square_medium) {
    imageSrc = item.imageSquare?.variations?.square_medium;
  }

  return (
    <li className="collection-ng__teaser-item js-teaser-ng-item">
      <a href={`/audio/${item.slug}`} className="teaser-ng js-teaser-ng">
        <div className="teaser-ng__medium">
          <picture className="image-ng">
            <img className="image-ng__img" alt="" src={imageSrc} />
          </picture>
        </div>
      </a>
    </li>
  );
};

const AudioEpisodesResults = ({
  searchResultEpisodes,
  searchContext,
  baseUrl,
  translate,
  loadMore,
  loading,
}) => {
  if (!searchContext.searchParams.q) {
    return null;
  }

  let title = translate('audioEpisodesTitle').replace(
    // quick and dirty: JMSTranslate has problems with '&', so this will be replaced here
    '%brandName%',
    searchContext.searchConfig.audioSearch.srfBrandName,
  );

  if (searchResultEpisodes?.total) {
    const locale = searchContext.searchConfig.locale;
    title += ` (${Number(searchResultEpisodes.total).toLocaleString(locale)})`;
  }

  return (
    <div className="episode-list">
      <div className="episode-list__content episode-list__content--with-max-width">
        <h3 className="episode-list__title">
          <span className="episode-list__title-em">
            {searchContext.searchParams.q}
          </span>
          {title}
        </h3>

        {searchResultEpisodes?.searchResultMediaList?.map((result, index) => (
          <AudioResult
            searchContext={searchContext}
            {...result}
            key={index}
            baseUrl={baseUrl}
            translate={translate}
          />
        ))}
        {searchResultEpisodes?.searchResultMediaList?.length === 0 && (
          <NoResults styleModifier="search-no-results--with-padding" />
        )}

        {searchResultEpisodes?.next && (
          <ShowMoreBar
            styleModifier="show-more-bar--audio-search"
            showMoreText={translate('showMoreResults')}
            onClick={loadMore}
            loadMoreButtonDisabled={loading}
            backToTopLink="#top"
            backToTopText={translate('backtotop')}
          />
        )}
      </div>
    </div>
  );
};
